import { useRef, useEffect } from 'react';
import { isIncrementFormatter, getCanIncrement, getFormatterIncrementTargets, doFormatterSafeParse, looksEqual, doFormatterIncrementByNudgeAmount, isValidNumber, doFormatterParse } from '../../formatter/formatter.utils.js';
import { SKIP_RECORDING, useInteractionHandler } from '../../hooks/use-interaction-handler.js';
import { useCallbackIfFlagEnabled } from '../../hooks/use-memoization.js';
import { forward, accept } from '../../utils/event.js';
import { take } from '../../utils/ref.js';

const useFormatter = ({ ref, formatter, value: valueIn, onChange: onChangeIn, onChangeRestricted, onKeyDown: onKeyDownIn, ...props }, shared) => {
    const lastIncrementTargets = useRef(null);
    useEffect(() => {
        if (shared) {
            shared.select = (el) => {
                if (lastIncrementTargets.current) {
                    const incrementTargets = take(lastIncrementTargets);
                    if (selectByIncrementTargets(formatter, incrementTargets, el))
                        return;
                }
                if (formatter.defaultSelection) {
                    const { start, end } = formatter.defaultSelection(el.value);
                    el.setSelectionRange(start, end);
                }
                else {
                    el.select();
                }
            };
        }
    }, [shared, formatter]);
    const value = formatter.format(valueIn);
    // Mark increment formatter inputs as spinbuttons so the up/down arrow contract is conveyed
    const spinbuttonProps = isIncrementFormatter(formatter)
        ? {
            role: 'spinbutton',
            'aria-valuetext': value,
            'aria-valuemin': formatter.min,
            'aria-valuemax': formatter.max,
            'aria-valuenow': isValidNumber(valueIn) ? valueIn : undefined,
        }
        : null;
    const onChange = (valueString) => {
        const update = (newValue) => doFormatterParse(formatter, valueString, newValue).value;
        const res = doFormatterSafeParse(formatter, valueString, valueIn, 'change', null);
        if (res) {
            if (res.callback) {
                res.callback(update, { commit: true });
            }
            else {
                if (res.value === valueIn) ;
                else {
                    onChangeIn(res.value, 'change');
                    return;
                }
                if (res.value !== res.parsedValue) {
                    onChangeRestricted?.(res.parsedValue, { value: res.value });
                }
            }
        }
        // TODO: should this be handled by the formatter?
        // reset back to the last value for both the res function and null cases
        ref.current.value = value;
    };
    const onKeyDown = useCallbackIfFlagEnabled((e) => {
        if (forward(e, onKeyDownIn) || !isIncrementFormatter(formatter)) {
            return SKIP_RECORDING;
        }
        switch (e.key) {
            case 'ArrowUp':
            case 'ArrowDown': {
                // break so we still accept the event (nudging is still the expected behavior even when
                // not allowed for this value, so we don't want it to do something else)
                if (!getCanIncrement('nudge', formatter, valueIn))
                    break;
                // note: not using `e.currentTarget` because our interaction tests require a data-testid
                // in order to find the element for the test, and I'd rather not have to force every
                // input to have a data-testid just for this.
                const el = ref.current;
                const big = e.shiftKey;
                const direction = e.key === 'ArrowDown' ? -1 : 1;
                const incrementTargets = getFormatterIncrementTargets(formatter, el);
                // Commit any changes the user may have already made
                shared?.commit?.();
                const doIncrement = (val) => doFormatterIncrementByNudgeAmount(formatter, val, direction, {
                    big,
                    incrementTargets,
                });
                const valueStr = el.value;
                const res = doFormatterSafeParse(formatter, valueStr, valueIn, 'nudge', e);
                // bail and accept the event
                if (!res)
                    break;
                if (res.callback) {
                    res.callback((val) => doIncrement(val).value, { commit: true });
                    // break and accept the event
                    break;
                }
                const newVal = doIncrement(res.value);
                if (!looksEqual(formatter, res.value, newVal.value)) {
                    onChangeIn(newVal.value, 'nudge');
                }
                if (newVal.value !== newVal.preClamped) {
                    onChangeRestricted?.(newVal.preClamped, { value: newVal.value });
                }
                lastIncrementTargets.current = incrementTargets;
                selectByIncrementTargets(formatter, incrementTargets, el);
                break;
            }
            default:
                return SKIP_RECORDING;
        }
        accept(e);
    }, [formatter, onChangeIn, onChangeRestricted, onKeyDownIn, ref, shared, valueIn], 'uxp_memoization_optimizations');
    const recordedOnKeyDown = useInteractionHandler(onKeyDown, {
        eventName: 'keydown',
        recordingKey: shared?.recordingKey,
    }, [onKeyDown]);
    return { ...spinbuttonProps, ...props, ref, value, onChange, onKeyDown: recordedOnKeyDown };
};
function selectByIncrementTargets(formatter, targets, el) {
    if (targets && isIncrementFormatter(formatter) && formatter.getSelection) {
        // Select the current increment targets
        const { start, end } = formatter.getSelection(el.value, targets);
        el.setSelectionRange(start, end);
        return true;
    }
    return false;
}

export { useFormatter };
//# sourceMappingURL=use-formatter.js.map
